import { DateTime } from 'luxon';
import { emptyArray } from '../../default-props/empty';

// Values mirrored from notification-hub.types.ts enums.
// This file must stay .js because make-global-selectors only discovers *.selectors.js files.
// Luxon works because it ships as compiled JS in node_modules; our .ts source files
// can't be resolved by the CJS require() used in the build script.
const DateCategory = {
  NEW: 'NEW',
  YESTERDAY: 'YESTERDAY',
  EARLIER_THIS_WEEK: 'EARLIER_THIS_WEEK',
  LAST_WEEK: 'LAST_WEEK',
  EARLIER: 'EARLIER',
};

const NotificationFilter = {
  ALL: 'ALL',
  UNREAD: 'UNREAD',
  CRITICAL: 'CRITICAL',
  WARNING: 'WARNING',
  INFO: 'INFO',
};

const NotificationPriority = {
  CRITICAL: 'CRITICAL',
  WARNING: 'WARNING',
  INFO: 'INFO',
};

const DATE_CATEGORY_LABELS = {
  [DateCategory.NEW]: 'New',
  [DateCategory.YESTERDAY]: 'Yesterday',
  [DateCategory.EARLIER_THIS_WEEK]: 'Earlier this week',
  [DateCategory.LAST_WEEK]: 'Last week',
  [DateCategory.EARLIER]: 'Earlier',
};

const DATE_CATEGORY_ORDER = [
  DateCategory.NEW,
  DateCategory.YESTERDAY,
  DateCategory.EARLIER_THIS_WEEK,
  DateCategory.LAST_WEEK,
  DateCategory.EARLIER,
];

const getDateCategory = (createdUtc) => {
  const now = DateTime.now();
  const created = DateTime.fromISO(createdUtc);
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.minus({ days: 1 });
  const startOfWeek = now.startOf('week');
  const startOfLastWeek = startOfWeek.minus({ weeks: 1 });

  if (created >= startOfToday) {
    return DateCategory.NEW;
  }
  if (created >= startOfYesterday) {
    return DateCategory.YESTERDAY;
  }
  if (created >= startOfWeek) {
    return DateCategory.EARLIER_THIS_WEEK;
  }
  if (created >= startOfLastWeek) {
    return DateCategory.LAST_WEEK;
  }
  return DateCategory.EARLIER;
};

const filterNotifications = (notifications, activeFilter) => {
  switch (activeFilter) {
    case NotificationFilter.ALL:
      return notifications.filter((n) => !n.isDismissed);
    case NotificationFilter.UNREAD:
      return notifications.filter((n) => !n.isRead && !n.isDismissed);
    case NotificationFilter.CRITICAL:
      return notifications.filter((n) => n.priority === NotificationPriority.CRITICAL && !n.isDismissed);
    case NotificationFilter.WARNING:
      return notifications.filter((n) => n.priority === NotificationPriority.WARNING && !n.isDismissed);
    case NotificationFilter.INFO:
      return notifications.filter((n) => n.priority === NotificationPriority.INFO && !n.isDismissed);
    default:
      return notifications.filter((n) => !n.isDismissed);
  }
};

const groupNotifications = (notifications) => {
  if (notifications.length === 0) {
    return emptyArray;
  }

  const groupMap = new Map();

  notifications.forEach((notification) => {
    const groupKey = `${notification.title}::${notification.description}`;
    if (groupMap.has(groupKey)) {
      const group = groupMap.get(groupKey);
      group.groupedNotifications.push(notification);
      group.groupCount = group.groupedNotifications.length;
      // Keep the most recent notification as the representative
      if (notification.createdUtc > group.notification.createdUtc) {
        group.notification = notification;
      }
    } else {
      groupMap.set(groupKey, {
        notification,
        groupCount: 1,
        groupedNotifications: [notification],
        isGrouped: false,
      });
    }
  });

  const grouped = Array.from(groupMap.values()).map((group) => ({
    ...group,
    isGrouped: group.groupCount > 1,
  }));

  // Sort by newest first
  grouped.sort((a, b) => {
    const dateA = DateTime.fromISO(a.notification.createdUtc);
    const dateB = DateTime.fromISO(b.notification.createdUtc);
    return dateB.toMillis() - dateA.toMillis();
  });

  return grouped;
};

export const selectIsOpen = (state) => state?.isOpen ?? false;

export const selectActiveFilter = (state) => state?.activeFilter ?? NotificationFilter.ALL;

export const selectAllNotifications = (state) => state?.notifications ?? emptyArray;

export const selectFilteredNotifications = (state) => {
  const notifications = selectAllNotifications(state);
  const activeFilter = selectActiveFilter(state);
  return filterNotifications(notifications, activeFilter);
};

export const selectUnreadCount = (state) => state?.unreadCount ?? 0;

export const selectCriticalCount = (state) => state?.criticalCount ?? 0;

export const selectWarningCount = (state) => state?.warningCount ?? 0;

export const selectInfoCount = (state) => state?.infoCount ?? 0;

export const selectGroupedFilteredNotifications = (state) => {
  const filtered = selectFilteredNotifications(state);
  return groupNotifications(filtered);
};

export const selectDateGroupedNotifications = (state) => {
  const grouped = selectGroupedFilteredNotifications(state);

  if (grouped.length === 0) {
    return emptyArray;
  }

  const categoryMap = new Map();

  grouped.forEach((groupedNotification) => {
    const category = getDateCategory(groupedNotification.notification.createdUtc);
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    categoryMap.get(category).push(groupedNotification);
  });

  return DATE_CATEGORY_ORDER.filter((category) => categoryMap.has(category)).map((category) => {
    const notifications = categoryMap.get(category);
    return {
      category,
      label: DATE_CATEGORY_LABELS[category],
      count: notifications.length,
      notifications,
    };
  });
};

export const selectIsLoading = (state) => state?.isLoading ?? false;

export const selectHasError = (state) => state?.hasError ?? false;
